import {
	useQuery,
	useMutation,
	UseQueryOptions,
	UseMutationOptions,
	UseSuspenseQueryOptions,
	useSuspenseQuery,
	useInfiniteQuery,
	UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useApiService } from '../contexts/api-context';
import type { APIRequestConfig } from '../services/api';

interface UseApiMutationOptions<T, V>
	extends Omit<UseMutationOptions<T, unknown, V>, 'mutationFn'> {}

export const useApiQuery = <T>(
	key: string[],
	url: string,
	config?: Omit<UseQueryOptions<T, Error>, 'queryFn' | 'queryKey'>,
) => {
	const { apiClient } = useApiService();
	return useQuery<T>({
		queryKey: key,
		queryFn: ({ signal }) => apiClient.get<T>(url, { signal }),
		...config,
	});
};

export const useSuspenseApiQuery = <T>(
	key: string[],
	url: string,
	config?: Omit<UseSuspenseQueryOptions<T, Error>, 'queryFn' | 'queryKey'>,
) => {
	const { apiClient } = useApiService();
	return useSuspenseQuery<T>({
		queryKey: key,
		queryFn: ({ signal }) => apiClient.get<T>(url, { signal }),
		...config,
	});
};

export const useApiMutation = <T, V>(
	url: string,
	method: 'POST' | 'PUT' | 'DELETE',
	config?: UseApiMutationOptions<T, V>,
	requestConfig?: APIRequestConfig,
) => {
	const { apiClient } = useApiService();

	return useMutation<T, unknown, V>({
		mutationFn: (data: V) => {
			if (method === 'POST') return apiClient.post<T>(url, data, requestConfig);
			if (method === 'PUT') return apiClient.put<T>(url, data, requestConfig);
			return apiClient.delete<T>(url, requestConfig);
		},
		...config,
	});
};

export const useApiInfiniteQuery = <T>(
	key: string[],
	url: string,
	config?: Omit<
		UseInfiniteQueryOptions<T, Error, T>,
		'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
	> & {
		params?: Record<string, any>;
		getNextPageParam: (lastPage: any, allPages: any[]) => number | undefined;
	},
) => {
	if (!config?.getNextPageParam) {
		throw new Error(
			'getNextPageParam function is required for useApiInfiniteQuery',
		);
	}

	const { apiClient } = useApiService();

	return useInfiniteQuery<T, Error, T>({
		queryKey: key,
		queryFn: async ({ pageParam = 1 }) => {
			const { page_no, ...otherParams } = config?.params || {};
			const params = {
				...otherParams,
				page_no: pageParam,
			};

			const response = await apiClient.get<T>(url, { params });
			return response;
		},
		getNextPageParam: config.getNextPageParam,
		initialPageParam: 1,
		...config,
	});
};
